import React from "react";
import {
  SignInButton,
  SignUpButton,
  SignOutButton,
  UserButton,
  PricingTable,
} from "@clerk/clerk-react";
import { Button } from "@/app/components/ui/button";
import {
  ArrowRight,
  Zap,
  Table as TableIcon,
  Settings2,
  CheckCircle2,
  Cuboid,
  MousePointer2,
  Maximize2,
  LogOut,
} from "lucide-react";
import {
  IS_DEV_MODE,
  useAuthSafe,
} from "@/app/providers/AuthProvider";
import { useLanguage } from "@/app/contexts/LanguageContext";

import { LanguageSwitcher } from "@/app/components/LanguageSwitcher";
// import { PricingTable } from '@/app/components/PricingTable'; // Removed custom component
import { toast } from "sonner";

// Imported Screenshots
const imgIsoBlock = "https://images.iso-block.com/isoblock.jpg";
const imgBlockSystem =
  "https://images.iso-block.com/blocksystem.jpg";
const imgSystemTable =
  "https://images.iso-block.com/systemtable.jpg";

interface LandingPageProps {
  onEnterApp?: () => void;
  onNavigate?: (
    route: "landing" | "impressum" | "privacy" | "terms",
  ) => void;
}

export function LandingPage({
  onEnterApp,
  onNavigate,
}: LandingPageProps) {
  const { t } = useLanguage();
  const auth = useAuthSafe();
  const isAuthenticated = auth?.isAuthenticated ?? false;
  const [isUpgrading, setIsUpgrading] = React.useState(false);

  const handleDevAuth = () => {
    if (onEnterApp) onEnterApp();
  };

  const handlePlanSelect = async (planId: string) => {
    if (!isAuthenticated) {
      // If not logged in, just scroll to top or open sign up
      // We'll let the user sign up first
      const signUpBtn = document.querySelector(
        'button[data-clerk-trigger="sign-up"]',
      );
      if (signUpBtn instanceof HTMLElement) {
        signUpBtn.click();
      } else {
        toast.info(
          "Please sign up or sign in to select a plan.",
        );
      }
      return;
    }

    if (planId === "free") {
      toast.info("You are already on the Free tier.");
      return;
    }

    setIsUpgrading(true);

    // Simulate API call / Payment flow
    await new Promise((resolve) => setTimeout(resolve, 2000));

    if (IS_DEV_MODE) {
      toast.success(`Plan ${planId} activated! (Dev Mode)`);
      setIsUpgrading(false);
      if (onEnterApp) onEnterApp();
    } else {
      // In a real app, this would redirect to Stripe or call an API to update Clerk metadata
      toast.success(`Plan upgrade initiated for ${planId}!`);
      toast.info(
        "In this demo, we cannot process real payments. Please contact support.",
      );
      setIsUpgrading(false);
    }
  };

  const AuthButtons = () => {
    if (IS_DEV_MODE) {
      return (
        <Button
          variant="ghost"
          className="text-slate-600 hover:text-purple-700 hover:bg-purple-50 hidden sm:inline-flex"
          onClick={handleDevAuth}
        >
          {t("common.signIn")}
        </Button>
      );
    }

    if (isAuthenticated) {
      return (
        <div className="flex items-center gap-2">
          <UserButton afterSignOutUrl="/" />
        </div>
      );
    }

    return (
      <SignInButton mode="modal">
        <Button
          variant="ghost"
          className="text-slate-600 hover:text-purple-700 hover:bg-purple-50 hidden sm:inline-flex"
        >
          {t("common.signIn")}
        </Button>
      </SignInButton>
    );
  };

  const PrimaryCtaButton = () => {
    if (IS_DEV_MODE) {
      return (
        <Button
          className="bg-purple-600 hover:bg-purple-700 text-white shadow-md hover:shadow-lg transition-all rounded-full px-6"
          onClick={handleDevAuth}
        >
          {t("common.startNow")}
        </Button>
      );
    }

    if (isAuthenticated) {
      // If authenticated but on landing page, it means they might lack permissions (basic plan)
      // or they manually navigated here.
      return (
        <Button className="bg-slate-200 hover:bg-slate-300 text-slate-700 shadow-sm transition-all rounded-full px-6 cursor-not-allowed">
          Access Restricted
        </Button>
      );
    }

    return (
      <SignUpButton mode="modal">
        <Button className="bg-purple-600 hover:bg-purple-700 text-white shadow-md hover:shadow-lg transition-all rounded-full px-6">
          {t("common.startNow")}
        </Button>
      </SignUpButton>
    );
  };

  const HeroCtaButton = () => {
    if (IS_DEV_MODE) {
      return (
        <Button
          size="lg"
          className="h-14 px-8 text-lg bg-purple-600 hover:bg-purple-700 text-white shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all rounded-xl w-full sm:w-auto group"
          onClick={handleDevAuth}
        >
          {t("landing.cta.getStarted")}
          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </Button>
      );
    }

    if (isAuthenticated) {
      return (
        <Button
          size="lg"
          className="h-14 px-8 text-lg bg-slate-100 text-slate-700 shadow-none rounded-xl w-full sm:w-auto cursor-not-allowed border border-slate-200"
        >
          {t("common.signIn")} (Active)
        </Button>
      );
    }

    return (
      <SignUpButton mode="modal">
        <Button
          size="lg"
          className="h-14 px-8 text-lg bg-purple-600 hover:bg-purple-700 text-white shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all rounded-xl w-full sm:w-auto group"
        >
          {t("landing.cta.getStarted")}
          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </Button>
      </SignUpButton>
    );
  };

  return (
    <div className="h-screen w-full overflow-y-auto overflow-x-hidden bg-slate-50 scroll-smooth">
      <div className="min-h-screen flex flex-col text-slate-900 font-sans selection:bg-purple-100 selection:text-purple-900">
        {/* --- Navigation --- */}
        <header className="sticky top-0 z-50 w-full bg-slate-50/80 backdrop-blur-md border-b border-slate-200/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-2 font-bold text-xl tracking-tight">
                <div className="bg-purple-600 text-white p-1.5 rounded-lg shadow-sm">
                  <Cuboid className="w-5 h-5" />
                </div>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-600">
                  Isoblock
                </span>
              </div>

              <div className="flex items-center gap-3">
                <LanguageSwitcher />
                <AuthButtons />
                <PrimaryCtaButton />
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1">
          {/* --- Hero Section (Split Screen) --- */}
          <section className="relative pt-12 pb-16 lg:pt-24 lg:pb-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
              {/* Left Column: Copy */}
              <div className="space-y-8 animate-in slide-in-from-left-4 duration-700 fade-in">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 text-purple-700 text-xs font-bold border border-purple-100 uppercase tracking-wide">
                  <Zap className="w-3 h-3 fill-current" />
                  {t("landing.efficiencyBooster")}
                </div>

                <h1
                  className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1]"
                  dangerouslySetInnerHTML={{
                    __html: t("landing.heroTitle"),
                  }}
                />

                <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-lg">
                  {t("landing.heroDescription")}
                </p>

                <div className="space-y-4">
                  {[
                    t("landing.features.browserReady"),
                    t("landing.features.autoBom"),
                    t("landing.features.parametric"),
                    t("landing.features.customParts"),
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 text-slate-700 font-medium"
                    >
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>

                <div className="pt-4 flex flex-col sm:flex-row gap-4">
                  <HeroCtaButton />
                </div>

                <p className="text-sm text-slate-400">
                  {t("landing.cta.noCreditCard")}
                </p>
              </div>

              {/* Right Column: Hero Image */}
              <div className="relative lg:-mr-16 animate-in slide-in-from-right-4 duration-1000 fade-in delay-200">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-white ring-1 ring-slate-900/5">
                  <div className="absolute top-0 left-0 right-0 h-8 bg-slate-100 border-b border-slate-200 flex items-center px-4 gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-amber-400" />
                    <div className="w-3 h-3 rounded-full bg-emerald-400" />
                  </div>
                  <div className="mt-8">
                    <img
                      src={imgBlockSystem}
                      alt="Isoblock Editor Interface"
                      className="w-full h-auto object-cover"
                    />
                  </div>

                  {/* Floating Badge */}
                  <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl border border-slate-100 hidden md:block animate-bounce-slow">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="text-sm font-bold text-slate-900">
                          {t("landing.badges.validationActive")}
                        </div>
                        <div className="text-xs text-slate-500">
                          {t(
                            "landing.badges.connectionsChecked",
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Background decorative elements */}
                <div className="absolute -z-10 top-1/2 right-0 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
                <div className="absolute -z-10 bottom-0 left-10 w-72 h-72 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
              </div>
            </div>
          </section>

          {/* --- Feature 1: Simplicity & Speed --- */}
          <section className="py-20 bg-white border-y border-slate-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid md:grid-cols-2 gap-16 items-center">
                <div className="order-2 md:order-1 relative rounded-xl overflow-hidden shadow-lg border border-slate-200 group">
                  <img
                    src={imgIsoBlock}
                    alt="Intuitive Zeichenwerkzeuge"
                    className="w-full h-auto transform transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-xl" />
                </div>

                <div className="order-1 md:order-2 space-y-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-2">
                    <MousePointer2 className="w-6 h-6" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900">
                    {t("landing.feature1.title")}
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {t("landing.feature1.description")}
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                      <span className="text-slate-700">
                        {t("landing.feature1.list.grid")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                      <span className="text-slate-700">
                        {t("landing.feature1.list.dragDrop")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                      <span className="text-slate-700">
                        {t("landing.feature1.list.autoDim")}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* --- Feature 2: Parametrics --- */}
          <section className="py-20 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid md:grid-cols-2 gap-16 items-center">
                <div className="space-y-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 mb-2">
                    <Settings2 className="w-6 h-6" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900">
                    {t("landing.feature2.title")}
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {t("landing.feature2.description")}
                  </p>
                  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <h4 className="font-semibold text-slate-900 mb-2">
                      {t("landing.feature2.boxTitle")}
                    </h4>
                    <p className="text-sm text-slate-500">
                      {t("landing.feature2.boxDescription")}
                    </p>
                  </div>
                </div>

                <div className="relative rounded-xl overflow-hidden shadow-lg border border-slate-200 group">
                  <img
                    src={imgBlockSystem}
                    alt="Parametrische Systemeinstellungen"
                    className="w-full h-auto transform transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-xl" />
                </div>
              </div>
            </div>
          </section>

          {/* --- Feature 3: Tables & Export --- */}
          <section className="py-20 bg-white border-y border-slate-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid md:grid-cols-2 gap-16 items-center">
                <div className="order-2 md:order-1 relative rounded-xl overflow-hidden shadow-lg border border-slate-200 group">
                  <img
                    src={imgSystemTable}
                    alt="Tabellarische Auswertung"
                    className="w-full h-auto transform transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-xl" />
                </div>

                <div className="order-1 md:order-2 space-y-6">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600 mb-2">
                    <TableIcon className="w-6 h-6" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900">
                    {t("landing.feature3.title")}
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {t("landing.feature3.description")}
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span className="text-slate-700">
                        {t("landing.feature3.list.sync")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span className="text-slate-700">
                        {t("landing.feature3.list.grouping")}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span className="text-slate-700">
                        {t("landing.feature3.list.export")}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* --- Pricing Section --- */}
          {!isAuthenticated && (
            <section className="py-20 bg-slate-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <PricingTable />
              </div>
            </section>
          )}

          {/* --- CTA Section --- */}
          <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop')] opacity-10 bg-cover bg-center" />
            <div className="relative max-w-4xl mx-auto px-4 text-center space-y-8">
              <h2 className="text-3xl sm:text-5xl font-bold tracking-tight">
                {t("landing.cta.readyForNextStep")}
              </h2>
              <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto">
                {t("landing.cta.startFree")}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                {IS_DEV_MODE ? (
                  <Button
                    size="lg"
                    className="h-14 px-8 text-lg bg-white text-purple-900 hover:bg-slate-100 shadow-xl hover:shadow-2xl transition-all w-full sm:w-auto rounded-xl font-bold"
                    onClick={handleDevAuth}
                  >
                    {t("landing.cta.registerFree")}
                  </Button>
                ) : (
                  <SignUpButton mode="modal">
                    <Button
                      size="lg"
                      className="h-14 px-8 text-lg bg-white text-purple-900 hover:bg-slate-100 shadow-xl hover:shadow-2xl transition-all w-full sm:w-auto rounded-xl font-bold"
                    >
                      {t("landing.cta.registerFree")}
                    </Button>
                  </SignUpButton>
                )}
                {IS_DEV_MODE ? (
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 px-8 text-lg border-slate-600 text-white hover:bg-slate-800 hover:text-white hover:border-slate-500 w-full sm:w-auto rounded-xl bg-transparent"
                    onClick={handleDevAuth}
                  >
                    {t("common.signIn")}
                  </Button>
                ) : (
                  <SignInButton mode="modal">
                    <Button
                      size="lg"
                      variant="outline"
                      className="h-14 px-8 text-lg border-slate-600 text-white hover:bg-slate-800 hover:text-white hover:border-slate-500 w-full sm:w-auto rounded-xl bg-transparent"
                    >
                      {t("common.signIn")}
                    </Button>
                  </SignInButton>
                )}
              </div>
            </div>
          </section>
        </main>

        {/* --- Footer --- */}
        <footer className="bg-white py-12 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2 font-bold text-lg text-slate-900">
              <Cuboid className="w-5 h-5 text-purple-600" />
              Isoblock
            </div>
            <p className="text-slate-500 text-sm">
              <span
                dangerouslySetInnerHTML={{
                  __html: t("landing.footer.rights").replace(
                    "{year}",
                    new Date().getFullYear().toString(),
                  ),
                }}
              />
            </p>
            <div className="flex gap-6 text-sm text-slate-500">
              <button
                onClick={() => onNavigate?.("impressum")}
                className="hover:text-purple-600 transition-colors text-left"
              >
                {t("landing.footer.imprint")}
              </button>
              <button
                onClick={() => onNavigate?.("privacy")}
                className="hover:text-purple-600 transition-colors text-left"
              >
                {t("landing.footer.privacy")}
              </button>
              <button
                onClick={() => onNavigate?.("terms")}
                className="hover:text-purple-600 transition-colors text-left"
              >
                {t("landing.footer.terms")}
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}